import { useFieldErrors } from '../hooks/useFieldErrors';
import { useState } from 'react';
import {
  makeStyles, tokens, Spinner, Badge, Button,
  Field, Input, Select, Textarea,
  
  Dialog, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '../i18n';
import { NumberInput } from '../components/shared/NumberInput';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { useEntityNames } from '../hooks/useEntityNames';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { useRanchoLotes } from '../hooks/useRanchoLotes';
import { useFsmaEnabled } from '../hooks/useFsmaEnabled';
import { CoolingKdeFields } from '../components/traceability/CoolingKdeFields';
import {
  fetchFolios, fetchFolio, createFolio, addFolioEvent,
  type TraceabilityFolio, type TraceabilityEvent, type FolioStatus,
  type TraceabilityEventType, type CreateFolioInput, type AddEventInput,
} from '../api/traceability.api';
import { PageTitle } from '../components/shared/PageTitle';
import { DataTable } from '../components/shared/DataTable';

const useStyles = makeStyles({
  root:     { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  filters:  { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'flex-end', flexWrap: 'wrap' },
  toolbar:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  table:    { width: '100%', overflowX: 'auto' as const },
  th:       { fontWeight: tokens.fontWeightSemibold, padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  td:       { padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`, textAlign: 'left' as const },
  timeline: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, padding: tokens.spacingVerticalS },
  eventRow: { display: 'flex', gap: tokens.spacingHorizontalS, fontSize: tokens.fontSizeBase200, alignItems: 'center' },
});

const EVENT_TYPES: TraceabilityEventType[] = [
  'FIELD_OP', 'HARVEST', 'PACKING', 'CONTAINER', 'CUSTOMS', 'EXPORT',
];

/** Tipos de evento disponibles: COOLING (FSMA 204) solo si la empresa tiene fsma.enabled. */
export function eventTypesFor(fsmaEnabled: boolean): TraceabilityEventType[] {
  return fsmaEnabled ? [...EVENT_TYPES, 'COOLING'] : EVENT_TYPES;
}
const STATUS_COLOR: Record<FolioStatus, 'success' | 'warning' | 'subtle' | 'danger'> = {
  OPEN:       'warning',
  IN_TRANSIT: 'warning',
  DELIVERED:  'success',
  CLOSED:     'subtle',
};

export function TraceabilityPage() {
  const t = useT();
  const { loteName } = useEntityNames();
  const { fmtNum, fmtDateTime } = useIntlFormat();
  const s = useStyles();
  const qc = useQueryClient();
  const { formatDate } = useLocaleSettings();

  const [loteId, setLoteId]   = useState('');
  const [selectedFolioId, setSelectedFolioId] = useState<string | null>(null);
  const [folioDialog, setFolioDialog] = useState(false);
  const ff = useFieldErrors();
  const fv = useFieldErrors();
  const [eventDialog, setEventDialog] = useState(false);
  const [folioForm, setFolioForm]     = useState<Partial<CreateFolioInput>>({});
  const [eventForm, setEventForm]     = useState<Partial<AddEventInput>>({});

  const fsmaEnabled = useFsmaEnabled();
  const { lotes } = useRanchoLotes(true, { value: loteId, reset: () => setLoteId('') });

  const { data: foliosData, isLoading: listLoading } = useQuery({
    queryKey: ['folios', loteId],
    queryFn:  () => fetchFolios(loteId),
    enabled:  !!loteId,
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['folio-detail', selectedFolioId],
    queryFn:  () => fetchFolio(selectedFolioId!),
    enabled:  !!selectedFolioId,
  });

  const mutFolio = useMutation({
    mutationFn: (input: CreateFolioInput) => createFolio(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folios', loteId] });
      setFolioDialog(false);
      setFolioForm({});
    },
  });

  const mutEvent = useMutation({
    mutationFn: (input: AddEventInput) => addFolioEvent(selectedFolioId!, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folio-detail', selectedFolioId] });
      setEventDialog(false);
      setEventForm({});
    },
  });

  const folios = foliosData?.folios ?? [];

  function statusLabel(st: FolioStatus): string {
    const key = `status${st}` as keyof typeof t.traceability;
    return String(t.traceability[key] ?? st);
  }
  function eventTypeLabel(et: TraceabilityEventType): string {
    const key = `event${et}` as keyof typeof t.traceability;
    return String(t.traceability[key] ?? et);
  }

  return (
    <div className={s.root}>
      <PageTitle>{t.traceability.title}</PageTitle>

      {/* Filtro lote */}
      <div className={s.filters}>
        <SearchableSelect
          label={t.traceability.loteLabel}
          placeholder={t.common.searchLotePlaceholder}
          options={lotes.map(l => ({ id: l.loteId, label: l.name }))}
          value={loteId}
          onChange={setLoteId}
          noResultsText={t.common.searchNoResults}
        />
      </div>

      {!loteId && <p style={{ color: tokens.colorNeutralForeground3 }}>{t.traceability.noData}</p>}

      {/* Toolbar + tabla folios */}
      {loteId && (
        <>
          <div className={s.toolbar}>
            <strong>{t.traceability.foliosTitle.replace('{lote}', loteName(loteId))}</strong>
            <Button appearance="primary" onClick={() => setFolioDialog(true)}>
              {t.traceability.addFolioBtn}
            </Button>
          </div>

          {listLoading && <Spinner size="small" label={t.traceability.loading} />}

          <DataTable<TraceabilityFolio>
            rows={listLoading ? [] : folios}
            rowKey={(f) => f.folioId}
            emptyMessage={listLoading ? '…' : t.traceability.noData}
            onRowClick={(f) => setSelectedFolioId(f.folioId)}
            columns={[
              { key: 'folioId', header: t.traceability.tableFolioId, render: (f) => <span style={{ fontFamily: 'monospace', fontSize: tokens.fontSizeBase200 }}>{f.folioId.slice(-8)}</span> },
              { key: 'product', header: t.traceability.tableProduct },
              { key: 'variety', header: t.traceability.tableVariety },
              { key: 'kg', header: t.traceability.tableEstKg, render: (f) => fmtNum(f.estimatedQuantityKg) },
              { key: 'status', header: t.traceability.tableStatus, render: (f) => <Badge appearance="tint" color={STATUS_COLOR[f.status]}>{statusLabel(f.status)}</Badge> },
              { key: 'createdAt', header: t.traceability.tableCreated, render: (f) => formatDate(f.createdAt) },
            ]}
          />
        </>
      )}

      {/* Dialog detalle folio */}
      <Dialog open={!!selectedFolioId} onOpenChange={(_, d) => { if (!d.open) { setSelectedFolioId(null); setEventForm({}); } }}>
        <DialogSurface style={{ maxWidth: 640 }}>
          <DialogTitle>
            {t.traceability.detailTitle}
            {detail?.folio && ` — ${detail.folio.product} (${detail.folio.variety})`}
          </DialogTitle>
          <DialogBody>
            <DialogContent>
              {detailLoading && <Spinner size="small" />}
              {detail && (
                <>
                  <p><strong>{t.traceability.tableEstKg}:</strong> {fmtNum(detail.folio.estimatedQuantityKg)} kg</p>
                  <p><strong>{t.traceability.tableStatus}:</strong> {statusLabel(detail.folio.status)}</p>
                  {detail.folio.destination && <p><strong>{t.traceability.destinationLabel}</strong> {detail.folio.destination}</p>}
                  {detail.folio.buyer && <p><strong>{t.traceability.buyerLabel}</strong> {detail.folio.buyer}</p>}

                  <h4>{t.traceability.detailTitle}</h4>
                  <div className={s.timeline}>
                    {detail.events.length === 0 && (
                      <span style={{ color: tokens.colorNeutralForeground3 }}>{t.traceability.detailNoEvents}</span>
                    )}
                    {detail.events.map((ev: TraceabilityEvent) => (
                      <div key={ev.eventId} className={s.eventRow}>
                        <span style={{ minWidth: 140 }}>{fmtDateTime(ev.occurredAt)}</span>
                        <Badge appearance="tint" size="small">{eventTypeLabel(ev.type)}</Badge>
                        <span>{ev.description}</span>
                        {ev.temperature !== undefined && <span>{ev.temperature} °C</span>}
                        {ev.quantityKg !== undefined && <span>{ev.quantityKg} kg</span>}
                      </div>
                    ))}
                  </div>

                  <Button
                    appearance="outline"
                    style={{ marginTop: tokens.spacingVerticalM }}
                    onClick={() => setEventDialog(true)}
                  >
                    {t.traceability.addEventBtn}
                  </Button>
                </>
              )}
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => setSelectedFolioId(null)}>{t.common.cancel}</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* Dialog crear folio */}
      <Dialog open={folioDialog} onOpenChange={(_, d) => { setFolioDialog(d.open); if (!d.open) { setFolioForm({}); ff.clear(); } }}>
        <DialogSurface>
          <DialogTitle>{t.traceability.addFolioBtn}</DialogTitle>
          <DialogBody>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Field label={t.traceability.formProduct} required {...ff.props('product')}>
                <Input value={folioForm.product ?? ''}
                  onChange={(_, d) => { setFolioForm(f => ({ ...f, product: d.value })); ff.clear('product'); }} />
              </Field>
              <Field label={t.traceability.formVariety} required {...ff.props('variety')}>
                <Input value={folioForm.variety ?? ''}
                  onChange={(_, d) => { setFolioForm(f => ({ ...f, variety: d.value })); ff.clear('variety'); }} />
              </Field>
              <Field label={t.traceability.formEstKg} required {...ff.props('estimatedQuantityKg')}>
                <NumberInput value={folioForm.estimatedQuantityKg}
                  onValueChange={(v) => { setFolioForm(f => ({ ...f, estimatedQuantityKg: parseFloat(v) })); ff.clear('estimatedQuantityKg'); }} />
              </Field>
              <Field label={t.traceability.formDestination}>
                <Input value={folioForm.destination ?? ''}
                  onChange={(_, d) => setFolioForm(f => ({ ...f, destination: d.value }))} />
              </Field>
              <Field label={t.traceability.formBuyer}>
                <Input value={folioForm.buyer ?? ''}
                  onChange={(_, d) => setFolioForm(f => ({ ...f, buyer: d.value }))} />
              </Field>
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => { setFolioDialog(false); setFolioForm({}); ff.clear(); }}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              disabled={mutFolio.isPending}
              onClick={() => {
                const req = t.common.validation.fieldRequired;
                const ok = ff.validate({
                  product: !folioForm.product?.trim() && req,
                  variety: !folioForm.variety?.trim() && req,
                  estimatedQuantityKg: !folioForm.estimatedQuantityKg && req,
                });
                if (ok) mutFolio.mutate({ loteId, ...folioForm } as CreateFolioInput);
              }}
            >{t.common.save}</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>

      {/* Dialog agregar evento */}
      <Dialog open={eventDialog} onOpenChange={(_, d) => { setEventDialog(d.open); if (!d.open) { setEventForm({}); fv.clear(); } }}>
        <DialogSurface>
          <DialogTitle>{t.traceability.addEventBtn}</DialogTitle>
          <DialogBody>
            <DialogContent style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <Field label={t.traceability.eventType} required {...fv.props('type')}>
                <Select
                  value={eventForm.type ?? ''}
                  onChange={(_, d) => { setEventForm(f => ({ ...f, type: d.value as TraceabilityEventType })); fv.clear('type'); }}
                >
                  <option value="">—</option>
                  {eventTypesFor(fsmaEnabled).map(et => (
                    <option key={et} value={et}>{eventTypeLabel(et)}</option>
                  ))}
                </Select>
              </Field>
              <Field label={t.traceability.eventDesc} required {...fv.props('description')}>
                <Textarea value={eventForm.description ?? ''}
                  onChange={(_, d) => { setEventForm(f => ({ ...f, description: d.value })); fv.clear('description'); }} />
              </Field>
              {fsmaEnabled && eventForm.type === 'COOLING' && (
                <CoolingKdeFields form={eventForm} onChange={patch => { setEventForm(f => ({ ...f, ...patch })); fv.clear('coolingLocation'); fv.clear('coolingDate'); }} />
              )}
              <Field label={t.traceability.eventKg}>
                <NumberInput value={eventForm.quantityKg}
                  onValueChange={(v) => setEventForm(f => ({ ...f, quantityKg: parseFloat(v) || undefined }))} />
              </Field>
              <Field label={t.traceability.eventTemp}>
                <Input type="number" value={String(eventForm.temperature ?? '')}
                  onChange={(_, d) => setEventForm(f => ({ ...f, temperature: parseFloat(d.value) || undefined }))} />
              </Field>
              <Field label={t.traceability.eventBy}>
                <Input value={eventForm.operator ?? ''}
                  onChange={(_, d) => setEventForm(f => ({ ...f, operator: d.value }))} />
              </Field>
            </DialogContent>
          </DialogBody>
          <DialogActions>
            <Button onClick={() => { setEventDialog(false); setEventForm({}); fv.clear(); }}>{t.common.cancel}</Button>
            <Button
              appearance="primary"
              disabled={mutEvent.isPending}
              onClick={() => {
                const req = t.common.validation.fieldRequired;
                const isCooling = eventForm.type === 'COOLING';
                const ok = fv.validate({
                  type: !eventForm.type && req,
                  description: !eventForm.description?.trim() && req,
                  ...(isCooling ? {
                    coolingLocation: !eventForm.coolingLocation?.trim() && req,
                    coolingDate: !eventForm.coolingDate?.trim() && req,
                  } : {}),
                });
                if (!ok || !selectedFolioId) return;
                mutEvent.mutate({ loteId, ...eventForm } as AddEventInput);
              }}
            >{t.common.save}</Button>
          </DialogActions>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
